import { Color3, Mesh, MeshBuilder, type Scene, TransformNode } from "@babylonjs/core";
import { STATUS_BITS } from "@rivalwatch/shared";
import { glow } from "./materials.ts";

type Part =
  | "stun" | "sleep" | "frozen" | "slow" | "root" | "marked" | "haste" | "strong"
  | "antiHealA" | "antiHealB" | "antiHealSlash" | "healA" | "healB" | "resist" | "boost" | "link" | "stealth";

export const STATUS_VISUAL_BITS = [
  STATUS_BITS.stun,
  STATUS_BITS.sleep,
  STATUS_BITS.frozen,
  STATUS_BITS.slow,
  STATUS_BITS.root,
  STATUS_BITS.marked,
  STATUS_BITS.haste,
  STATUS_BITS.unstoppable,
  STATUS_BITS.invuln,
  STATUS_BITS.antiHeal,
  STATUS_BITS.healBoost,
  STATUS_BITS.resist,
  STATUS_BITS.boost,
  STATUS_BITS.linked,
  STATUS_BITS.bonded,
  STATUS_BITS.stealth,
] as const;

const visible = (m: Mesh, on: boolean) => m.setEnabled(on);

/** Cheap, generic status presentation attached to one avatar; meshes are built once and toggled. */
export class StatusFx {
  readonly root: TransformNode;
  private readonly parts = new Map<Part, Mesh>();
  private t = 0;

  constructor(
    private readonly scene: Scene,
    parent: TransformNode,
    private readonly height: number,
    private readonly radius: number,
  ) {
    this.root = new TransformNode("status-fx", scene);
    this.root.parent = parent;
    this.build();
  }

  private mesh(k: Part, m: Mesh, color: string, alpha = 0.75) {
    m.material = glow(this.scene, color, alpha);
    m.isPickable = false;
    m.setEnabled(false);
    this.parts.set(k, m);
    return m;
  }

  private build() {
    const r = Math.max(0.35, this.radius);
    const h = this.height;
    this.mesh("stun", MeshBuilder.CreateTorus("stun-stars", { diameter: r * 2.4, thickness: 0.035, tessellation: 24 }, this.scene), "#ffe680", 0.9).position.y = h + 0.38;
    const sleepBubbles = [0, 1, 2].map((i) => {
      const s = MeshBuilder.CreateSphere("sleep-bubble", { diameter: 0.16 + i * 0.06, segments: 8 }, this.scene);
      s.position.set(i * 0.22, i * 0.16, 0);
      return s;
    });
    this.mesh("sleep", Mesh.MergeMeshes(sleepBubbles, true, true, undefined, false, true)!, "#b9a8ff", 0.9).position.set(-r, h + 0.45, 0);
    this.mesh("frozen", MeshBuilder.CreateBox("ice-block", { width: r * 2.9, height: h * 1.05, depth: r * 2.9 }, this.scene), "#9ff3ff", 0.22).position.y = h * 0.52;
    this.mesh("slow", MeshBuilder.CreateTorus("slow-ring", { diameter: r * 2.8, thickness: 0.035, tessellation: 32 }, this.scene), "#9ff3ff", 0.7).position.y = 0.05;
    this.mesh("root", MeshBuilder.CreateTorus("root-shackle", { diameter: r * 2.1, thickness: 0.06, tessellation: 24 }, this.scene), "#d6a15a", 0.8).position.y = 0.12;
    this.mesh("marked", MeshBuilder.CreatePolyhedron("marked", { type: 1, size: 0.24 }, this.scene), "#ff3355", 0.95).position.y = h + 0.72;
    const streak = this.mesh("haste", MeshBuilder.CreateBox("haste-streaks", { width: r * 1.8, height: 0.04, depth: 0.04 }, this.scene), "#7dff8a", 0.55);
    streak.position.set(0, h * 0.45, r * 1.4);
    const strong = this.mesh("strong", MeshBuilder.CreateTorus("strong-rim", { diameter: r * 3, thickness: 0.045, tessellation: 40 }, this.scene), "#fff4d0", 0.85);
    strong.position.y = h * 0.55;
    strong.rotation.x = Math.PI / 2;
    const a = this.mesh("antiHealA", MeshBuilder.CreateBox("antiheal-a", { width: 0.42, height: 0.08, depth: 0.04 }, this.scene), "#b45bff", 0.9);
    const b = this.mesh("antiHealB", MeshBuilder.CreateBox("antiheal-b", { width: 0.08, height: 0.42, depth: 0.04 }, this.scene), "#b45bff", 0.9);
    const slash = this.mesh("antiHealSlash", MeshBuilder.CreateBox("antiheal-slash", { width: 0.54, height: 0.06, depth: 0.04 }, this.scene), "#ff3355", 0.9);
    for (const m of [a, b, slash]) m.position.y = h + 0.42;
    slash.rotation.z = Math.PI / 4;
    const ha = this.mesh("healA", MeshBuilder.CreateBox("heal-a", { width: 0.34, height: 0.07, depth: 0.04 }, this.scene), "#7dff8a", 0.85);
    const hb = this.mesh("healB", MeshBuilder.CreateBox("heal-b", { width: 0.34, height: 0.07, depth: 0.04 }, this.scene), "#7dff8a", 0.85);
    ha.position.set(-0.11, h + 0.37, 0);
    hb.position.set(0.11, h + 0.37, 0);
    ha.rotation.z = -0.65;
    hb.rotation.z = 0.65;
    this.mesh("resist", MeshBuilder.CreateTorus("resist-shield", { diameter: 0.55, thickness: 0.045, tessellation: 24 }, this.scene), "#2fb4ff", 0.8).position.y = h + 0.28;
    this.mesh("boost", MeshBuilder.CreateSphere("boost-aura", { diameter: r * 3.2, segments: 16 }, this.scene), "#ff3355", 0.17).position.y = h * 0.55;
    this.mesh("link", MeshBuilder.CreateSphere("link-glow", { diameter: r * 2.7, segments: 12 }, this.scene), "#8a6cff", 0.16).position.y = h * 0.55;
    this.mesh("stealth", MeshBuilder.CreateSphere("stealth-shimmer", { diameter: r * 2.6, segments: 16 }, this.scene), "#9ff3ff", 0.18).position.y = h * 0.52;
    for (const m of this.parts.values()) {
      m.parent = this.root;
      m.renderOutline = false;
    }
    this.parts.get("frozen")!.enableEdgesRendering(0.95);
    this.parts.get("frozen")!.edgesColor = Color3.FromHexString("#d7fbff").toColor4(0.75);
  }

  update(dt: number, status: number, speed: number, mode: "normal" | "stealth-shimmer" | "hidden", opacity = 1) {
    this.t += dt;
    const shimmerOnly = mode === "stealth-shimmer";
    const hidden = mode === "hidden";
    const on = (p: Part, yes: boolean) => {
      const m = this.parts.get(p)!;
      m.visibility = opacity;
      visible(m, !hidden && (shimmerOnly ? p === "stealth" : yes));
    };
    on("stun", !!(status & STATUS_BITS.stun));
    on("sleep", !!(status & STATUS_BITS.sleep));
    on("frozen", !!(status & STATUS_BITS.frozen));
    on("slow", !!(status & STATUS_BITS.slow));
    on("root", !!(status & STATUS_BITS.root));
    on("marked", !!(status & STATUS_BITS.marked));
    on("haste", !!(status & STATUS_BITS.haste) && speed > 1);
    on("strong", !!(status & (STATUS_BITS.unstoppable | STATUS_BITS.invuln)));
    on("antiHealA", !!(status & STATUS_BITS.antiHeal));
    on("antiHealB", !!(status & STATUS_BITS.antiHeal));
    on("antiHealSlash", !!(status & STATUS_BITS.antiHeal));
    on("healA", !!(status & STATUS_BITS.healBoost));
    on("healB", !!(status & STATUS_BITS.healBoost));
    on("resist", !!(status & STATUS_BITS.resist));
    on("boost", !!(status & STATUS_BITS.boost));
    on("link", !!(status & (STATUS_BITS.linked | STATUS_BITS.bonded)));
    on("stealth", !!(status & STATUS_BITS.stealth) || shimmerOnly);

    const bob = Math.sin(this.t * 5) * 0.04;
    this.parts.get("stun")!.rotation.y += dt * 4;
    this.parts.get("sleep")!.position.y = this.height + 0.55 + bob;
    this.parts.get("marked")!.rotation.y += dt * 2.5;
    this.parts.get("marked")!.position.y = this.height + 0.72 + bob;
    this.parts.get("slow")!.rotation.y += dt * 1.5;
    this.parts.get("root")!.rotation.y -= dt;
    this.parts.get("haste")!.rotation.y += dt * 8;
    this.parts.get("strong")!.scaling.setAll(1 + Math.sin(this.t * 7) * 0.04);
    this.parts.get("stealth")!.scaling.setAll(1 + Math.sin(this.t * 9) * 0.04);
    this.parts.get("boost")!.scaling.setAll(1 + Math.sin(this.t * 6) * 0.03);
  }

  dispose() {
    this.root.dispose(false, true);
  }
}
